import { createContext, useContext, useEffect, useState, type ReactNode } from 'react'
import type { PulseOpen } from './pulseDrawer.js'

const TITLES: Record<PulseOpen['kind'], string> = {
  document: 'Document',
  change_order: 'Change order',
  pay_app: 'Pay application',
  payments: 'Accounting',
}

export const PulseDrawerActionContext = createContext<(node: ReactNode) => void>(() => {})

export function usePulseDrawerAction(factory: () => ReactNode, deps: unknown[], enabled = true) {
  const setAction = useContext(PulseDrawerActionContext)
  useEffect(() => {
    if (!enabled) {
      setAction(null)
      return undefined
    }
    setAction(factory())
    return () => setAction(null)
    // factory is recreated each render; deps are the stable inputs
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, setAction, ...deps])
}

export default function PulseDrawer({
  open,
  onClose,
  children,
}: {
  open: PulseOpen | null
  onClose: () => void
  children: ReactNode
}) {
  const isOpen = open != null
  const [action, setAction] = useState<ReactNode>(null)
  useEffect(() => {
    if (!isOpen) setAction(null)
  }, [isOpen, open?.kind])
  useEffect(() => {
    if (!isOpen) return
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [isOpen, onClose])

  return (
    <PulseDrawerActionContext.Provider value={setAction}>
      <div className={`pm-back${isOpen ? ' on' : ''}`} onClick={onClose} aria-hidden="true" />
      <div
        className={`pm${isOpen ? ' on' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label={open ? TITLES[open.kind] : 'Record'}
        data-testid="pulse-drawer"
        hidden={!isOpen}
      >
        <div className="pulse-drawer-head" data-testid="pulse-drawer-head">
          <div className="pulse-drawer-kind" data-testid="pulse-drawer-kind">{open ? TITLES[open.kind] : ''}</div>
          <div className="pulse-drawer-head-actions">
            {action}
            <button type="button" className="btn b-ol btn-sm pulse-drawer-close" data-testid="pulse-drawer-close" onClick={onClose} aria-label="Close">✕</button>
          </div>
        </div>
        <div className="pm-body pulse-drawer-body">
          {isOpen ? children : null}
        </div>
      </div>
    </PulseDrawerActionContext.Provider>
  )
}
